import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { Ablageort, ApiClient, Unternehmen, VorgangsBezug, VorgangsDatei } from "@kundeninfo/api-client";
import { useBestaetigung, useSofortPopup } from "@kundeninfo/ui";
import { Chip, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { formatDatum } from "../format.js";
import { keepInView } from "../scroll.js";
import { UnternehmensChipsAnzeige } from "../views/UnternehmensZuordnung.js";
import { KiKnopf, LoeschKnopf } from "./meHuellen.js";
import { apiMeldung, speichereBlob } from "./vorgangsdetail.js";
import { UploadFilesPanel } from "./documentFiles/UploadFilesPanel.js";
import { LinkFilesPanel } from "./documentFiles/LinkFilesPanel.js";
import { ReadOnlyMarker } from "./documentFiles/ReadOnlyMarker.js";
import { DOCUMENT_FILE_TEXT as T } from "./documentFiles/texts.js";
import "./documentFiles/documentFiles.css";

/**
 * Der Abschnitt „DATEIEN“ am Vorgang (Spec „Oberfläche › Abschnitt ‚Dateien‘“, Entscheide 463, 469, 470; F3, F6/F7): ein
 * Baustein für Angebot, Auftrag und Rechnung. Er kennt seinen Vorgang nur über `bezug`; der Einbau (Aufgabe 25) setzt
 * ihn in einen Vorgangsabschnitt der Objektart `datei` und liest den Zähler über `onZahl`.
 *
 *  - **Bedienbar ohne Bearbeiten-Panel.** Dateien drehen die Belegversion nicht; jede Änderung geht sofort an den
 *    Server und fragt vorher (Hausregel „Bestätigung vor Datenänderung“): Lösen und Löschen gestapelt über
 *    `useBestaetigung`, Hochladen und Verknüpfen im Panel, dessen Knopf die Bestätigung ist.
 *  - **Lesend** (`lesend` = Text des Sofort-Popups, an einer älteren Angebotsversion): kein Hochladen, kein Verknüpfen,
 *    keine Aktionen — Herunterladen und der Sprung in den Ordner bleiben.
 *  - **Herkunft** als rahmenloses Kennzeichen: Beleg-PDFs (keine Verknüpfungszeile, Lösen ergäbe 409 BELEG_PDF_FEST)
 *    und am Auftrag die lesend mitgezeigten Dateien des Herkunftsangebots („aus Angebot ‹Nr›“) haben nur das
 *    Herunterladen. „Datei löschen“ gibt es nur ohne weitere Verknüpfung (470). Eine Belegdatei eines Lieferantenvorgangs
 *    ist ohne Lieferantensicht nur lesbar (`readOnly`, Entscheid 518): kein Lösen, an seiner Stelle „nur lesend“.
 *  - **Scroll bei Neuzeichnen** (Hausregel): Die Lage des Bausteins wird vor einem Schreibweg (bzw. beim Öffnen eines
 *    Panels, hinter dem die Seite still steht) gemerkt und nach dem Neuzeichnen ausgeglichen (`keepInView`).
 */
export type DocumentFilesClient = Pick<ApiClient,
  "listDocumentFiles" | "uploadDocumentFiles" | "linkDocumentFile" | "unlinkDocumentFile" | "downloadFile"
  | "listFolders" | "listFiles" | "listFileTypes">;

export type VorgangsdateienProps = {
  client: DocumentFilesClient;
  kundeId: string;
  bezug: VorgangsBezug;
  /** Wie der Vorgang in den Sätzen heißt („Angebot ANG-2026-0003“). */
  vorgangName: string;
  /** Die sichtbaren Unternehmen — Kürzel und Hausfarbe der Chips. */
  firmen: readonly Unternehmen[];
  /** Text des Sofort-Popups am Kopf, wenn der Abschnitt nur lesend ist; sonst `null`. */
  lesend: string | null;
  /** Zahl der Zeilen nach jedem Laden — für den Zähler im Kopf des Abschnitts. */
  onZahl: (n: number) => void;
  /** Sprung „Ordner“ in den Kundenreiter „Dateien“. */
  onOrdnerOeffnen: (folderId: string) => void;
  /** Nach jedem Schreibweg (Hochladen, Verknüpfen, Lösen, Löschen) — im Objektpanel gilt die Ebene damit als geändert, die
   *  Ansicht darunter (Reiter Dateien, Kundenübersicht) lädt nach dem Schließen nach (Spec F9). */
  onGeaendert?: () => void;
};

type SectionState = { items: VorgangsDatei[]; storage: Ablageort };

/** Das Kennzeichen der Herkunft; die eigene Verknüpfung trägt keins. */
function originMarker(z: VorgangsDatei): string | null {
  if (z.origin === "DOCUMENT_PDF") return T.documentPdf(z.versionNumber);
  if (z.origin === "SOURCE_OFFER") return T.fromOffer(z.sourceOffer?.displayNumber ?? "");
  return null;
}

export function Vorgangsdateien(p: VorgangsdateienProps): ReactElement {
  const { client, kundeId, bezug } = p;
  const [data, setData] = useState<SectionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState<"upload" | "link" | null>(null);
  const { bestaetigen } = useBestaetigung();
  const popup = useSofortPopup<HTMLElement>(p.lesend ?? "");
  const root = useRef<HTMLDivElement | null>(null);
  /** Die vor dem Schreibweg gemerkte Lage; nach dem Neuzeichnen ausgeglichen. */
  const remembered = useRef<(() => void) | null>(null);
  const restore = useRef<(() => void) | null>(null);
  useLayoutEffect(() => { restore.current?.(); restore.current = null; });
  // Rückruf per Ref: Der Einbauort gibt ihn als Pfeilfunktion — am Abhängigkeitsarray lüde jedes Zeichnen neu.
  const reportCount = useRef(p.onZahl);
  reportCount.current = p.onZahl;
  const changed = (): void => { p.onGeaendert?.(); };
  const openPanel = useRef(panel);
  openPanel.current = panel;
  const key = `${bezug.art}/${bezug.id}`;

  const remember = (): void => { if (root.current !== null) remembered.current = keepInView(root.current); };

  /** Lädt den Stand; eine Meldung des vorangegangenen Schreibwegs erscheint im selben Zug wie der neue Stand. */
  const load = useCallback(async (message: string | null = null): Promise<void> => {
    try {
      const next = await client.listDocumentFiles(kundeId, bezug);
      restore.current = remembered.current;
      // Hinter einem offenen Panel steht die Seite still — die Lage gilt bis zu seinem Schließen.
      if (openPanel.current === null) remembered.current = null;
      setData(next);
      setError(message);
      reportCount.current(next.items.length);
    } catch (e) {
      setError(message ?? apiMeldung(e, T.loadFailed));
    }
    // `key` statt `bezug`: Ein Objektliteral im JSX ist bei jedem Zeichnen ein neues.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, kundeId, key]);

  // Geladen wird beim Einhängen; lazy macht es der Einbauort, der den Baustein erst beim Öffnen einhängt
  // (`filesContent`, vorgangsabschnitt/sections.tsx).
  useEffect(() => { void load(); }, [load]);

  /** Ein Schreibweg aus der Tabelle: Lage merken, schreiben, neu laden (auch nach einem Fehler — der Stand kann
   *  veraltet sein, etwa DATEI_MEHRFACH_VERKNUEPFT nach einer neuen Verknüpfung anderswo). */
  async function write(work: () => Promise<void>, fallback: string): Promise<void> {
    remember();
    setBusy(true);
    let message: string | null = null;
    try { await work(); } catch (e) { message = apiMeldung(e, fallback); }
    await load(message);
    setBusy(false);
    changed();
  }

  async function unlink(z: VorgangsDatei): Promise<void> {
    if (!(await bestaetigen({ titel: T.unlinkTitle, text: T.unlinkText(z.file.originalName, p.vorgangName),
      bestaetigenText: T.unlinkConfirm }))) return;
    await write(() => client.unlinkDocumentFile(kundeId, bezug, z.file.id), T.unlinkFailed);
  }

  async function remove(z: VorgangsDatei): Promise<void> {
    if (!(await bestaetigen({ titel: T.deleteTitle, text: T.deleteText(z.file.originalName, [p.vorgangName], 0),
      bestaetigenText: T.deleteConfirm, gefaehrlich: true }))) return;
    await write(() => client.unlinkDocumentFile(kundeId, bezug, z.file.id, { delete: true }), T.deleteFailed);
  }

  async function download(z: VorgangsDatei): Promise<void> {
    try {
      const { blob, filename } = await client.downloadFile(kundeId, z.file.id);
      speichereBlob(blob, filename ?? z.file.originalName);
    } catch (e) {
      setError(apiMeldung(e, T.downloadFailed(z.file.originalName)));
    }
  }

  function open(which: "upload" | "link"): void {
    remember();
    setPanel(which);
  }

  function close(): void {
    setPanel(null);
    remembered.current = null;
  }

  const readOnly = p.lesend !== null;
  const disabled = busy || panel !== null;

  function renderRow(z: VorgangsDatei): ReactElement {
    const name = z.file.originalName;
    const marker = originMarker(z);
    const folderId = z.file.folderId;
    const path = z.folderPath.length > 0 ? z.folderPath : [T.rootFolder];
    // Nur die eigene Verknüpfung ist zu lösen, und nur, wenn die Datei nicht gesperrt ist (518); lesend fehlt die Spalte.
    const changeable = z.origin === "LINK" && !z.readOnly;
    return (
      <tr key={`${z.origin}:${z.file.id}`} data-file-id={z.file.id}>
        <td>
          <KiKnopf art="leise" name={T.download(name)} onClick={() => { void download(z); }}>{name}</KiKnopf>
        </td>
        <td>{marker !== null && <Chip>{marker}</Chip>}</td>
        <td>
          {folderId === null ? <span className="doc-files__muted">{T.rootFolder}</span> : (
            <KiKnopf kompakt name={T.openFolder(path)} onClick={() => p.onOrdnerOeffnen(folderId)}>
              {T.path(path)}
            </KiKnopf>
          )}
        </td>
        <td>
          <UnternehmensChipsAnzeige alle={p.firmen} companyIds={z.file.companyIds} className="doc-files__companies" />
        </td>
        <td>{z.linkedAt === null ? null : T.linkedBy(formatDatum(z.linkedAt), z.linkedByName)}</td>
        {!readOnly && (
          <td>
            {changeable && (
              <span className="doc-files__actions">
                <KiKnopf kompakt name={T.unlinkActionName(name)} deaktiviert={disabled}
                  onClick={() => { void unlink(z); }}>{T.unlinkAction}</KiKnopf>
                {z.otherLinkCount === 0 && (
                  <LoeschKnopf name={T.deleteActionName(name)} deaktiviert={disabled}
                    onClick={() => { void remove(z); }} />
                )}
              </span>
            )}
            {z.readOnly && <ReadOnlyMarker />}
          </td>
        )}
      </tr>
    );
  }

  return (
    <div ref={root} className="doc-files">
      <div className="doc-files__toolbar">
        {readOnly ? <Chip ref={popup}>{T.readOnly}</Chip> : (
          <>
            <KiKnopf deaktiviert={disabled || data === null} onClick={() => open("upload")}>{T.upload}</KiKnopf>
            <KiKnopf deaktiviert={disabled || data === null} onClick={() => open("link")}>{T.linkExisting}</KiKnopf>
          </>
        )}
      </div>

      {/* SCHLECHTESTES OBEN (Hausregel): der Befund vor der Liste. */}
      {error !== null && <Hinweis art="fehler">{error}</Hinweis>}

      {data === null && error === null && <p className="doc-files__muted" role="status">{T.loading}</p>}
      {data !== null && data.items.length === 0 && <p className="doc-files__muted">{T.empty}</p>}
      {data !== null && data.items.length > 0 && (
        <Tabelle stapeln dicht className="doc-files__table">
          <table>
            <thead>
              <tr>
                <th>{T.columnFile}</th>
                <th>{T.columnOrigin}</th>
                <th>{T.columnFolder}</th>
                <th>{T.columnCompanies}</th>
                <th>{T.columnLinked}</th>
                {!readOnly && <th>{T.columnActions}</th>}
              </tr>
            </thead>
            <tbody>{data.items.map(renderRow)}</tbody>
          </table>
        </Tabelle>
      )}

      {panel === "upload" && data !== null && (
        <UploadFilesPanel client={client} kundeId={kundeId} bezug={bezug} storage={data.storage}
          onChanged={async () => { await load(); changed(); }} onClose={close} />
      )}
      {panel === "link" && data !== null && (
        <LinkFilesPanel client={client} kundeId={kundeId} bezug={bezug}
          // „aus Angebot“ hängt am Angebot, nicht hier: am Auftrag ausdrücklich verknüpfbar (F6).
          linkedIds={new Set(data.items.filter((z) => z.origin !== "SOURCE_OFFER").map((z) => z.file.id))}
          onChanged={async () => { await load(); changed(); }} onClose={close} />
      )}
    </div>
  );
}
